/**
 * Client-side multi-select facet filtering for blog listing pages.
 *
 * Shopify's native blog tag routing (/tagged/tag1+tag2) can only AND tags
 * together, so it can't support "show me Sewing OR Knitcraft" checkbox
 * behaviour. This filters the articles that are already rendered on the
 * page (see sections/main-blog.liquid) instead, entirely in the browser:
 * OR within a facet, AND across facets, with live per-option counts.
 *
 * Because it's client-side, it only ever sees the articles rendered in
 * the page (capped by the "Maximum articles to load for filtering"
 * section setting) - it isn't a substitute for a real search index if a
 * blog grows past that cap.
 *
 * The full matching set is still computed against every loaded article
 * (see loadRemainingPages below) so counts/filters stay correct across the
 * whole blog - only the DISPLAY is paginated into "Results per page"-sized
 * windows with page links, entirely client-side (no reload/refetch).
 */
(function () {
  var FACETS = ['craft', 'type', 'occasion', 'time', 'difficulty'];

  function init() {
    var root = document.querySelector('[data-blog-filter-root]');
    if (!root) return;

    var items = Array.prototype.slice.call(root.querySelectorAll('[data-blog-filter-item]'));
    var checkboxes = Array.prototype.slice.call(root.querySelectorAll('.blog-filter-checkbox'));
    if (!items.length || !checkboxes.length) return;

    var resultCountEl = root.querySelector('[data-blog-filter-result-count]');
    var emptyStateEl = root.querySelector('[data-blog-filter-empty]');
    var clearAllBtn = root.querySelector('[data-blog-filter-clear]');
    var articlesGrid = root.querySelector('.blog-articles');
    var paginationEl = root.querySelector('[data-blog-filter-pagination]');
    var paginationArrowTemplate = root.querySelector('[data-blog-filter-pagination-arrow]');

    // Shopify's own pagination state (how much of the catalog is already
    // in the DOM vs. still to be fetched by loadRemainingPages() below) -
    // distinct from the display-side pagination totals computed in apply().
    var shopifyCurrentPage = parseInt(root.dataset.blogFilterCurrentPage, 10) || 1;
    var shopifyTotalPages = parseInt(root.dataset.blogFilterTotalPages, 10) || 1;

    // Starts true (instead of false) whenever more of the catalog is still
    // due to arrive, so the very first apply() call - which runs before
    // loadRemainingPages() below even starts fetching - already knows to
    // hold off on permanently clamping a restored/typed page number that
    // only looks out of range because the rest of the catalog hasn't
    // loaded in yet.
    var isLoadingMore = shopifyTotalPages > shopifyCurrentPage;

    // Display-only pagination over the filtered result set - distinct from
    // loadRemainingPages()'s background Shopify page fetches below, which
    // keep loading every article regardless of what's currently shown.
    var PAGE_SIZE = parseInt(root.dataset.blogFilterResultsPerPage, 10) || 12;
    var currentResultPage = 1;

    // Remember each checkbox's plain-text label so counts can be appended
    // to it without re-reading/mutating the base label every time. Guarded
    // so one unexpected checkbox/markup mismatch can't abort init() before
    // the change listeners below get attached.
    checkboxes.forEach(function (checkbox) {
      var wrappingLabel = checkbox.closest('label');
      var textEl = wrappingLabel ? wrappingLabel.querySelector('.facet-checkbox__text') : null;
      if (!textEl) return;
      checkbox._labelEl = textEl;
      checkbox._baseLabel = textEl.textContent.trim();
    });

    function valuesOf(item, facet) {
      var raw = item.getAttribute('data-' + facet) || '';
      return raw.split(' ').filter(Boolean);
    }

    // A checked "art" should also match an article tagged "art__watercolour".
    function itemHasValue(item, facet, value) {
      var values = valuesOf(item, facet);
      if (values.indexOf(value) !== -1) return true;
      var prefix = value + '__';
      for (var i = 0; i < values.length; i++) {
        if (values[i].indexOf(prefix) === 0) return true;
      }
      return false;
    }

    function getChecked() {
      var checked = {};
      FACETS.forEach(function (facet) {
        checked[facet] = [];
      });
      checkboxes.forEach(function (checkbox) {
        if (checkbox.checked) checked[checkbox.dataset.facet].push(checkbox.dataset.value);
      });
      return checked;
    }

    function itemMatches(item, checked, ignoreFacet) {
      for (var i = 0; i < FACETS.length; i++) {
        var facet = FACETS[i];
        if (facet === ignoreFacet) continue;
        var values = checked[facet];
        if (!values.length) continue;
        var matchesAny = values.some(function (value) {
          return itemHasValue(item, facet, value);
        });
        if (!matchesAny) return false;
      }
      return true;
    }

    function updateCounts(checked) {
      // Every checkbox within a facet shares the same "which items pass
      // every OTHER facet's filter" answer, so compute that once per
      // facet (4 passes over items) instead of once per checkbox (was
      // ~checkbox-count passes over items) - with a few hundred items and
      // a few dozen checkboxes this is the difference between a click
      // feeling instant and visibly lagging.
      var candidatesByFacet = {};
      FACETS.forEach(function (facet) {
        candidatesByFacet[facet] = items.filter(function (item) {
          return itemMatches(item, checked, facet);
        });
      });

      checkboxes.forEach(function (checkbox) {
        var facet = checkbox.dataset.facet;
        var value = checkbox.dataset.value;
        var candidates = candidatesByFacet[facet];
        var count = 0;
        for (var i = 0; i < candidates.length; i++) {
          if (itemHasValue(candidates[i], facet, value)) count += 1;
        }
        if (checkbox._labelEl) {
          checkbox._labelEl.textContent = checkbox._baseLabel + ' (' + count + ')';
        }
        var disable = count === 0 && !checkbox.checked;
        checkbox.disabled = disable;
        var wrappingItem = checkbox.closest('li');
        if (wrappingItem) wrappingItem.classList.toggle('facet-checkbox--disabled', disable);
      });
    }

    function updateSelectedBadges(checked) {
      FACETS.forEach(function (facet) {
        var badge = root.querySelector('[data-blog-filter-selected="' + facet + '"]');
        if (!badge) return;
        var count = checked[facet].length;
        badge.textContent = count ? ' (' + count + ')' : '';
      });
    }

    function syncUrl(checked) {
      var params = new URLSearchParams();
      FACETS.forEach(function (facet) {
        if (checked[facet].length) params.set(facet, checked[facet].join(','));
      });
      // Result page rides along in the URL too, so clicking an article and
      // pressing Back lands where the visitor actually was - without this,
      // any back-navigation that isn't served from bfcache (inconsistent
      // across browsers, hence the "happens randomly" of it) re-runs this
      // script from scratch with no way to know they were on page 6.
      if (currentResultPage > 1) params.set('page', String(currentResultPage));
      var query = params.toString();
      var newUrl = window.location.pathname + (query ? '?' + query : '');
      window.history.replaceState(null, '', newUrl);
    }

    // Builds the "Prev 1 2 … 8 Next" control, reusing the theme's native
    // pagination__item/pagination__list classes (component-pagination.css)
    // so this looks consistent with the rest of the site even though it's
    // <button>s driving a client-side slice instead of <a> page reloads.
    function renderPagination(totalPages, displayPage) {
      if (!paginationEl) return;
      paginationEl.innerHTML = '';

      if (totalPages <= 1) {
        paginationEl.hidden = true;
        return;
      }
      paginationEl.hidden = false;

      var nav = document.createElement('nav');
      nav.className = 'pagination';
      nav.setAttribute('role', 'navigation');
      nav.setAttribute('aria-label', 'Pagination');

      var list = document.createElement('ul');
      list.className = 'pagination__list list-unstyled';
      list.setAttribute('role', 'list');
      nav.appendChild(list);

      function goToPage(page) {
        currentResultPage = page;
        apply();
        // Land at the very top of the section (title + filters included),
        // not just above the grid - a "next page" click should read like
        // landing on a fresh page, not a small in-place jump.
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      function addItem(label, page, options) {
        options = options || {};
        var li = document.createElement('li');

        if (options.ellipsis) {
          var span = document.createElement('span');
          span.className = 'pagination__item pagination__item--ellipsis';
          span.setAttribute('aria-hidden', 'true');
          span.textContent = label;
          li.appendChild(span);
          list.appendChild(li);
          return;
        }

        var btn = document.createElement('button');
        btn.type = 'button';
        var classNames = ['pagination__item'];
        if (options.current) classNames.push('pagination__item--current');
        // Reuses the native pagination markup's (confusingly inverted, but
        // already-shipped) class names: --next rotates the caret to point
        // left for "previous", --prev rotates it to point right for "next"
        // - see the rotate() rules in component-pagination.css.
        if (options.arrow) classNames.push('pagination__item-arrow', 'pagination__item--' + options.arrow);
        btn.className = classNames.join(' ');

        if (options.arrow && paginationArrowTemplate) {
          btn.appendChild(paginationArrowTemplate.content.cloneNode(true));
          var icon = btn.querySelector('svg');
          if (icon) icon.setAttribute('aria-hidden', 'true');
        } else {
          btn.textContent = label;
        }
        if (options.ariaLabel) btn.setAttribute('aria-label', options.ariaLabel);

        if (options.current) {
          btn.setAttribute('aria-current', 'page');
          btn.disabled = true;
        } else if (options.disabled) {
          btn.disabled = true;
        } else {
          btn.addEventListener('click', function () {
            goToPage(page);
          });
        }

        li.appendChild(btn);
        list.appendChild(li);
      }

      addItem('Prev', displayPage - 1, {
        disabled: displayPage <= 1,
        ariaLabel: 'Previous page',
        arrow: 'next',
      });

      // A full run of page numbers doesn't scale once a filtered set spans
      // dozens of pages, so only a window around the current page (plus
      // first/last) is shown, with an ellipsis filling any gap.
      var lastShown = 0;
      for (var page = 1; page <= totalPages; page++) {
        if (page !== 1 && page !== totalPages && Math.abs(page - displayPage) > 1) continue;
        if (lastShown && page - lastShown > 1) addItem('…', null, { ellipsis: true });
        addItem(String(page), page, {
          current: page === displayPage,
          ariaLabel: 'Page ' + page,
        });
        lastShown = page;
      }

      addItem('Next', displayPage + 1, {
        disabled: displayPage >= totalPages,
        ariaLabel: 'Next page',
        arrow: 'prev',
      });

      paginationEl.appendChild(nav);
    }

    function apply() {
      var checked = getChecked();

      var matching = items.filter(function (item) {
        return itemMatches(item, checked, null);
      });

      var totalCount = matching.length;
      var totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

      // Only permanently move the target page while every background chunk
      // has either loaded or given up retrying. A page restored from the
      // URL (e.g. page 6, after clicking an article and coming back) can
      // look "out of range" for a moment if it's read before the rest of
      // the catalog has finished loading in - clamping it for good at that
      // instant would silently strand the visitor on page 1 even though
      // page 6 becomes perfectly valid a second later.
      if (!isLoadingMore) {
        if (currentResultPage > totalPages) currentResultPage = totalPages;
        if (currentResultPage < 1) currentResultPage = 1;
      }

      // What's actually rendered this pass - clamped locally so a
      // still-loading target page never breaks the slice/pagination math.
      var displayPage = Math.min(Math.max(currentResultPage, 1), totalPages);

      var startIndex = (displayPage - 1) * PAGE_SIZE;
      var endIndex = startIndex + PAGE_SIZE;
      var visibleOnPage = matching.slice(startIndex, endIndex);
      var visibleSet = new Set(visibleOnPage);

      items.forEach(function (item) {
        item.hidden = !visibleSet.has(item);
      });

      updateCounts(checked);
      updateSelectedBadges(checked);
      syncUrl(checked);
      renderPagination(totalPages, displayPage);

      if (resultCountEl) {
        if (totalCount === 0) {
          resultCountEl.textContent = '0 projects';
        } else {
          var rangeStart = startIndex + 1;
          var rangeEnd = Math.min(endIndex, totalCount);
          resultCountEl.textContent =
            'Showing ' +
            rangeStart +
            '–' +
            rangeEnd +
            ' of ' +
            totalCount +
            (totalCount === 1 ? ' project' : ' projects');
        }
        if (isLoadingMore) resultCountEl.textContent += ' · loading more…';
      }
      if (emptyStateEl) {
        // While a background chunk is still loading, a 0-result state is
        // unconfirmed - the match a customer is after may simply not have
        // arrived yet. Don't tell them "no matches" until loading is done.
        emptyStateEl.hidden = totalCount !== 0 || isLoadingMore;
      }
    }

    // Shopify hard-caps a single paginate call at 250 articles, so a blog
    // with more than that needs the remaining pages fetched separately.
    // This pulls them in the background (via the Section Rendering API)
    // right after the first page renders, so filtering/counts are correct
    // across the whole blog rather than only whatever fit in page one -
    // the trade-off is that the full article set still ends up on the
    // page, just via a couple of requests instead of one.
    function loadRemainingPages() {
      var sectionId = root.dataset.blogFilterSectionId;
      if (!sectionId || !articlesGrid) {
        // Nothing can actually be fetched here regardless of what the page
        // counts said - don't leave isLoadingMore stuck true forever (that
        // would wedge the empty-state message and the page-clamp guard on
        // permanently).
        if (isLoadingMore) {
          isLoadingMore = false;
          apply();
        }
        return;
      }
      if (!isLoadingMore) return;

      var failedPages = [];

      function fetchPageHtml(page) {
        var url = window.location.pathname + '?section_id=' + encodeURIComponent(sectionId) + '&page=' + page;
        return fetch(url).then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        });
      }

      // Retries a page up to 3 attempts (with a short backoff) before
      // giving up on it - a single dropped request must not silently
      // remove a page's worth of articles from what customers can filter.
      function fetchPageWithRetry(page, attempt) {
        attempt = attempt || 1;
        return fetchPageHtml(page)
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var newItems = Array.prototype.slice.call(parsed.querySelectorAll('[data-blog-filter-item]'));
            newItems.forEach(function (item) {
              // Scroll-reveal animation relies on an observer set up for
              // elements present at page load - strip it from anything
              // added later so it just renders normally instead of
              // possibly getting stuck pre-animation (invisible).
              item.classList.remove('scroll-trigger', 'animate--slide-in');
              item.removeAttribute('data-cascade');
              item.style.removeProperty('--animation-order');
              articlesGrid.appendChild(item);
              items.push(item);
            });
          })
          .catch(function (error) {
            if (attempt < 3) {
              return new Promise(function (resolve) {
                setTimeout(resolve, 600 * attempt);
              }).then(function () {
                return fetchPageWithRetry(page, attempt + 1);
              });
            }
            console.error('[blog-filters] gave up loading page ' + page + ' after ' + attempt + ' attempts:', error);
            failedPages.push(page);
          });
      }

      // "Done" only ever means every page either loaded or exhausted its
      // retries - never silently claim completeness while a page is
      // actually still missing.
      function showRetryPromptIfNeeded() {
        var existing = root.querySelector('[data-blog-filter-retry]');
        if (existing) existing.remove();
        if (!failedPages.length || !resultCountEl) return;

        var retryBtn = document.createElement('button');
        retryBtn.type = 'button';
        retryBtn.className = 'link underlined-link blog-filter-retry';
        retryBtn.setAttribute('data-blog-filter-retry', '');
        retryBtn.textContent = 'Some projects didn’t load – tap to retry';
        retryBtn.addEventListener('click', function () {
          var pagesToRetry = failedPages.slice();
          failedPages = [];
          retryBtn.remove();
          loadPages(pagesToRetry);
        });
        resultCountEl.insertAdjacentElement('afterend', retryBtn);
      }

      // Pages are independent requests, so there's no reason to wait for
      // page 2 to finish before starting page 3 - fetching them in
      // parallel means total wait time is bounded by the slowest single
      // page instead of the sum of every page (this was the main source
      // of the multi-second wait with more than one remaining page).
      // apply() still runs as each one lands, so results keep appearing
      // incrementally rather than all arriving at once.
      function loadPages(pages) {
        if (!pages.length) return;

        isLoadingMore = true;
        apply();

        var pending = pages.length;
        pages.forEach(function (page) {
          fetchPageWithRetry(page).then(function () {
            pending -= 1;
            apply();
            if (pending === 0) {
              isLoadingMore = false;
              apply();
              showRetryPromptIfNeeded();
            }
          });
        });
      }

      var pagesToLoad = [];
      for (var page = shopifyCurrentPage + 1; page <= shopifyTotalPages; page++) pagesToLoad.push(page);
      loadPages(pagesToLoad);
    }

    function restoreFromUrl() {
      var params = new URLSearchParams(window.location.search);
      FACETS.forEach(function (facet) {
        var raw = params.get(facet);
        if (!raw) return;
        var values = raw.split(',');
        checkboxes.forEach(function (checkbox) {
          if (checkbox.dataset.facet === facet && values.indexOf(checkbox.dataset.value) !== -1) {
            checkbox.checked = true;
          }
        });
      });

      var pageParam = parseInt(params.get('page'), 10);
      currentResultPage = pageParam > 0 ? pageParam : 1;
    }

    // Defers the actual recompute to the next frame so the checkbox's own
    // checked-state change paints immediately rather than waiting behind
    // it - the click itself should never feel like it's the slow part. A
    // changed filter always jumps back to result page 1 (the old page
    // number may no longer exist, or may now show a confusingly different
    // set); that reset is remembered across a batch of same-frame changes
    // via pendingReset so it isn't lost if scheduleApply(true) is called
    // more than once before the frame runs.
    var applyFrame = null;
    var pendingReset = false;
    function scheduleApply(resetPage) {
      if (resetPage) pendingReset = true;
      if (applyFrame) return;
      applyFrame = window.requestAnimationFrame(function () {
        applyFrame = null;
        var shouldReset = pendingReset;
        pendingReset = false;
        if (shouldReset) currentResultPage = 1;
        apply();
      });
    }

    checkboxes.forEach(function (checkbox) {
      checkbox.addEventListener('change', function () {
        scheduleApply(true);
      });
    });

    if (clearAllBtn) {
      clearAllBtn.addEventListener('click', function () {
        checkboxes.forEach(function (checkbox) {
          checkbox.checked = false;
        });
        scheduleApply(true);
      });
    }

    window.addEventListener('popstate', function () {
      checkboxes.forEach(function (checkbox) {
        checkbox.checked = false;
      });
      // restoreFromUrl() sets currentResultPage from the URL's own page
      // param now, so this always lands on whatever page that history
      // entry actually represents instead of assuming page 1.
      restoreFromUrl();
      apply();
    });

    restoreFromUrl();
    apply();
    loadRemainingPages();
  }

  // Mobile "Filter" button -> slide-in drawer, independent of the
  // filtering logic above so a markup mismatch in one doesn't take out
  // the other.
  function initMobileDrawer() {
    var root = document.querySelector('[data-blog-filter-root]');
    if (!root) return;

    var trigger = root.querySelector('[data-blog-filter-open]');
    var drawer = root.querySelector('.blog-filter-sidebar');
    var closeBtn = root.querySelector('[data-blog-filter-close]');
    var backdrop = document.querySelector('[data-blog-filter-backdrop]');
    if (!trigger || !drawer || !backdrop) return;

    var lastFocused = null;

    function isOpen() {
      return drawer.classList.contains('is-open');
    }

    function openDrawer() {
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      backdrop.classList.add('is-open');
      backdrop.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      backdrop.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') closeDrawer();
    }

    trigger.addEventListener('click', function () {
      if (isOpen()) {
        closeDrawer();
      } else {
        openDrawer();
      }
    });

    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    backdrop.addEventListener('click', closeDrawer);

    // Don't leave the drawer stuck "open" (with scroll locked) if the
    // viewport is resized up past the point where it's rendered inline.
    window.addEventListener('resize', function () {
      if (window.innerWidth >= 750 && isOpen()) closeDrawer();
    });
  }

  function safeInit() {
    try {
      init();
    } catch (error) {
      // Surface this clearly rather than silently leaving every checkbox
      // inert - if you see this in the console, that's the bug to fix.
      console.error('[blog-filters] failed to initialise:', error);
    }
    try {
      initMobileDrawer();
    } catch (error) {
      console.error('[blog-filters] mobile drawer failed to initialise:', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeInit);
  } else {
    safeInit();
  }
})();
